import { useEffect, useRef, useState, MouseEventHandler } from 'react'

import { useTranslation } from 'react-i18next'
import { Container, Theme, useMediaQuery } from '@mui/material'
import Image from 'next/image'
import { Carousel } from 'react-responsive-carousel'
import 'react-responsive-carousel/lib/styles/carousel.min.css'
import lottie from 'lottie-web'
import { AnimationItem } from 'lottie-web'

import { useGetPlantTypes } from '@/services/plant-type/hooks'
import ArrowUpIcon from 'public/images/arrow-up.svg'
import PlantTypeInterface from '@/model/interfaces/plant-type'
import LeftArrow from 'public/images/left-arrow-carrusel.svg'
import RightArrow from 'public/images/right-arrow-carrusell.svg'
import sparks from 'public/animations/sparks.json'
import PlantAnimationInterface from '@/model/interfaces/plant-animation'

import ComponentStyled from './styled'
import { getAnimationBySlug } from './utils'

const PLANT_TYPES_PARAMS = '?populate[0]=icon&populate[1]=image&populate[2]=animation'

type Props = {
  onClickPlant?: () => void
}

const Plants = ({ onClickPlant }: Props): JSX.Element => {
  const { t } = useTranslation()
  const isTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'))
  const [activePlant, setActivePlant] = useState<PlantTypeInterface>()
  const [showCarousel, setShowCarousel] = useState(false)
  const [startAnimation, setStartAnimation] = useState(true)
  const [animationList, setAnimationList] = useState<PlantAnimationInterface[]>([])
  const [sparkAnimation, setSparkAnimation] = useState<AnimationItem | null>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const plantsWrapperRef = useRef<HTMLDivElement>(null)
  const refs = useRef<HTMLDivElement[]>([])
  refs.current = []

  const { data: plants } = useGetPlantTypes(PLANT_TYPES_PARAMS)

  const handleClickPlant = (plant: PlantTypeInterface, ref: HTMLDivElement, index: number) => {
    setActiveIndex(index)
    if (ref) {
      setTimeout(
        () => {
          ref.scrollIntoView({ inline: 'center', block: 'start', behavior: 'smooth' })
          animationList.map(anim => {
            if (anim.id === plant.id) {
              anim?.animation?.play()
            } else {
              anim?.animation?.stop()
            }
          })
        },
        activePlant ? 10 : 750
      )
      setActivePlant(plant)
      if (onClickPlant) {
        onClickPlant()
      }
    }

    if (isTablet) {
      setShowCarousel(!showCarousel)
    }
  }

  const addToRefs = (el: HTMLDivElement) => {
    if (el && !refs.current.includes(el)) {
      refs.current.push(el)
    }
  }

  const initAnimation = (plant: PlantTypeInterface) => {
    const container = document.querySelector(`#plant-animation-${plant.id}`) || null
    const containerResponsive = document.querySelector(`#plant-animation-responsive-${plant.id}`) || null
    const animationData = getAnimationBySlug(plant.attributes.slug)

    const instance = {
      id: plant.id,
      animation: container
        ? lottie.loadAnimation({
            container,
            animationData,
            renderer: 'svg',
            loop: true,
            autoplay: false,
          })
        : undefined,
      responsive: containerResponsive
        ? lottie.loadAnimation({
            container: containerResponsive,
            animationData,
            renderer: 'svg',
            loop: true,
            autoplay: true,
          })
        : undefined,
    }

    if (instance.animation) instance?.animation?.stop()

    return instance
  }

  useEffect(() => {
    if (activePlant && animationList && animationList.some(anim => anim.responsive !== null)) {
      animationList?.filter(anim => anim.id === activePlant.id)[0]?.responsive?.play()
    }

    if (sparkAnimation && activePlant) {
      sparkAnimation.stop()
      setTimeout(() => sparkAnimation.play(), 400)
    } else {
      const container = document.querySelector(`#sparks-animation`)

      if (container) {
        const anim = lottie.loadAnimation({
          container,
          animationData: sparks,
          renderer: 'svg',
          loop: true,
          autoplay: false,
        })

        anim.stop()
        setSparkAnimation(anim)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePlant])

  useEffect(() => {
    if (startAnimation && plants.length !== animationList.length) {
      const list = [...animationList]
      plants?.map(plant => {
        list.push(initAnimation(plant))
      })

      setAnimationList(list)

      if (plants.length === animationList.length) {
        setStartAnimation(false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startAnimation, plants, activePlant])

  const renderIndicator = (onClick: MouseEventHandler<HTMLLIElement>, selected: boolean) => (
    <li onClick={onClick} className={selected ? 'wishes-indicator-selected' : 'wishes-indicator'}></li>
  )

  return (
    <ComponentStyled isActivePlant={activePlant ? true : false} showCarousel={showCarousel}>
      <div className='plants-content'>
        <div id={'sparks-animation'} />
        <Container maxWidth='xl' className='header-container'>
          {!activePlant && <TopContentInfo />}
        </Container>
        <Container maxWidth='xl' className='plants-container' disableGutters>
          <div className={'plants-wrapper'} ref={plantsWrapperRef}>
            {plants?.map((plant, i) => (
              <div
                className={`plant  ${plant.id === activePlant?.id && !isTablet ? 'active' : ''}`}
                ref={addToRefs}
                key={plant.id}
                onClick={() => handleClickPlant(plant, refs.current[i], i)}
              >
                <PlantContent plant={plant} activePlant={activePlant} />
              </div>
            ))}
          </div>
          {isTablet && (
            <div className='plants-wrapper-mobile'>
              <Carousel
                swipeable
                showThumbs={false}
                showStatus={false}
                renderIndicator={renderIndicator}
                renderArrowPrev={renderArrowPrev}
                renderArrowNext={renderArrowNext}
                selectedItem={activeIndex}
              >
                {plants?.map(plant => (
                  <div key={plant.id} className='wishes-step'>
                    <div className={'plant-content'}>
                      <div className='plant-info-content'>
                        <p className='plant-title'>{activePlant?.attributes.title}</p>
                        {activePlant?.attributes?.description && (
                          <p className='plant-desc'>{activePlant?.attributes?.description}</p>
                        )}
                        {activePlant?.attributes?.power_description && (
                          <div className='plant-superpower'>
                            <div className='plant-superpower-icon'>
                              <Image src='/images/sp.svg' alt={t('superpower')} objectFit='cover' layout='fill' />
                            </div>
                            <p className='plant-superpower-text'>{activePlant?.attributes?.power_description}</p>
                          </div>
                        )}
                      </div>
                      <div id={`plant-animation-responsive-${plant.id}`} className='plant-animation-container'></div>
                    </div>
                  </div>
                ))}
              </Carousel>
            </div>
          )}
        </Container>
      </div>
    </ComponentStyled>
  )
}

const renderArrowPrev = (onClick: MouseEventHandler<HTMLLIElement>, enabled: boolean) =>
  enabled ? (
    <span className='wishes-arrow wishes-arrow-left' onClick={onClick}>
      <LeftArrow />
    </span>
  ) : null

const renderArrowNext = (onClick: MouseEventHandler<HTMLLIElement>, enabled: boolean) =>
  enabled ? (
    <span className='wishes-arrow wishes-arrow-right' onClick={onClick}>
      <RightArrow />
    </span>
  ) : null

const TopContentInfo = () => {
  const { t } = useTranslation()

  return (
    <div className='top-content'>
      <h1 className='plants-title'>{t('why it is important to conserve crop diversity?')}</h1>
      <p className='plants-desc'>
        {t(
          'Crop diversity is essential for life on Earth. It sustains nearly everything we eat and drink. But, sadly, it’s rapidly disappearing. The conservation of crop diversity is a critical part of the solution to the world’s climate crisis, loss of global biodiversity and food and nutritional insecurity.'
        )}
      </p>
    </div>
  )
}

const PlantContent = ({ activePlant, plant }: PlantContentProps) => {
  const { t } = useTranslation()
  return (
    <div className={'plant-content'}>
      {activePlant?.id === plant.id && (
        <div className='plant-info-content'>
          <p className='plant-title'>{activePlant?.attributes.title}</p>
          {activePlant?.attributes?.description && <p className='plant-desc'>{activePlant?.attributes?.description}</p>}
          {activePlant?.attributes?.power_description && (
            <div className='plant-superpower'>
              <div className='plant-superpower-icon'>
                <Image src='/images/sp.svg' alt={t('superpower')} objectFit='cover' layout='fill' />
              </div>
              <p className='plant-superpower-text'>{activePlant?.attributes?.power_description}</p>
            </div>
          )}
        </div>
      )}
      <div className='plant-img'>
        <div id={`plant-animation-${plant.id}`}></div>
      </div>
      {activePlant?.id !== plant.id && (
        <div className='plant-name'>
          <ArrowUpIcon className='arrow' />
          <p>{plant.attributes.title}</p>
        </div>
      )}
    </div>
  )
}

type PlantContentProps = {
  activePlant?: PlantTypeInterface
  plant: PlantTypeInterface
}

export default Plants
